import { FC, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { AppConfig } from '@/config/app.config';
import useMediaQuery from '@/hooks/useMediaQuery';
import { useUserActivityChartApi } from '../hooks/useUserActivityChartApi';
import { Skeleton } from '@/components/ui/skeleton';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { ChartConfig } from '@/components/ui/chart';
import { useTranslation } from 'react-i18next';
import { useSuperAdminDashboardFilter } from '../store/useSuperAdminDashboardFilter';
import { ChartHelper } from '@/utils/ChartHelper';

type customChartProps = {
    [key: string]: {
        unit?: string;
    };
} & ChartConfig;

const CHART_CONFIG = {
    chart: {
        label: 'main.superAdmin.dashboard.components.chart.title',
        color: '#2EB566',
        unit: 'main.superAdmin.dashboard.components.chart.logs',
    },
} satisfies customChartProps;

const loadingData = [
    {
        month: new Date('2025-1-1'),
        value: 60,
    },
    {
        month: new Date('2025-2-1'),
        value: 90,
    },
    {
        month: new Date('2025-3-1'),
        value: 40,
    },
    {
        month: new Date('2025-4-1'),
        value: 60,
    },
    {
        month: new Date('2025-5-1'),
        value: 20,
    },
    {
        month: new Date('2025-6-1'),
        value: 80,
    },
];

type Props = {
    className?: string;
};

const SuperAdminDashboardCharts: FC<Props> = ({ className }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);

    const { userActivityChartData, isLoadingUserActivityChartData } = useUserActivityChartApi();

    const { label, color, unit } = CHART_CONFIG['chart'];

    const { filter } = useSuperAdminDashboardFilter();
    const { reportPeriod } = filter;

    const step = useMemo(
        () => ChartHelper.getChartStep(userActivityChartData?.value ?? 0),
        [userActivityChartData],
    );

    const maxYValue = useMemo(() => {
        if (isLoadingUserActivityChartData) return 100;
        return ChartHelper.getChartMaxDomainValue(userActivityChartData?.periodValues || [], step);
    }, [isLoadingUserActivityChartData, userActivityChartData, step]);

    return (
        <div
            className={cn(
                'p-6 flex flex-col gap-6 border border-popover-100 rounded-2.5xl bg-popover-10 select-none',
                isMobile && 'p-0 border-0 bg-popover-30 gap-4',
                className,
            )}
        >
            {!isMobile && (
                <div className="flex items-center gap-4">
                    <h4 className="text-xl font-medium">{t(label)}</h4>
                </div>
            )}
            {isLoadingUserActivityChartData ? (
                <div className="flex flex-col gap-1">
                    <Skeleton className="h-4 w-30 my-2" />
                </div>
            ) : (
                <div className="flex flex-col gap-1">
                    <span className={cn('text-2xl font-medium', isMobile && 'text-xl')}>
                        {`${userActivityChartData?.value} ${t(unit)}`}
                    </span>
                </div>
            )}
            <ChartContainer config={CHART_CONFIG} className="h-64 w-full">
                <BarChart
                    accessibilityLayer
                    data={
                        isLoadingUserActivityChartData
                            ? loadingData
                            : userActivityChartData?.periodValues
                    }
                    className={cn(isLoadingUserActivityChartData && 'animate-pulse')}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="month"
                        axisLine={{ stroke: '#EAEAEA' }}
                        tickLine={{ stroke: '#EAEAEA' }}
                        tickMargin={10}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) =>
                            ChartHelper.formatChartPeriod(value, reportPeriod)
                        }
                    />
                    <YAxis
                        allowDataOverflow={true}
                        scale="linear"
                        domain={[0, maxYValue]}
                        axisLine={{ stroke: '#EAEAEA' }}
                        tickLine={{ stroke: '#EAEAEA' }}
                        tickMargin={10}
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => value}
                    />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar
                        dataKey="value"
                        radius={[20, 20, 0, 0]}
                        fill={isLoadingUserActivityChartData ? '#eaeaea' : color}
                        maxBarSize={12}
                    />
                </BarChart>
            </ChartContainer>
        </div>
    );
};

export default SuperAdminDashboardCharts;
